import { FC, useMemo } from 'react'
import { useGetPartnerAllList } from 'framework/api/methods'

import { SimpleSelect } from 'components/atoms/Select'

export interface PartnerFilterOption {
  id: string
  value: string
  label: string
  code: string
}

export const ALL_PARTNERS_OPTION: PartnerFilterOption = {
  id: '',
  value: '',
  label: 'All partners',
  code: '',
}

export interface PartnerFilterDropdownProps {
  value: PartnerFilterOption
  onChange: (option: PartnerFilterOption) => void
}

const formatOptionLabel = (option: PartnerFilterOption) => (
  <div className='flex items-center gap-2'>
    <span className='font-semibold text-N-800 truncate'>{option.label}</span>
    {option.code && (
      <span className='text-[11px] font-semibold text-N-500 bg-N-100 rounded px-2 py-[1px]'>
        {option.code}
      </span>
    )}
  </div>
)

// react-select's default filterOption only matches against `label`; the design's "Search partners…"
// behaviour also needs to match the partner code, so a custom filterOption is required.
const filterOption = (
  candidate: { label: string; value: string; data: PartnerFilterOption },
  input: string,
) => {
  if (!input) return true
  const term = input.toLowerCase()
  return (
    candidate.data.label.toLowerCase().includes(term) ||
    candidate.data.code.toLowerCase().includes(term)
  )
}

export const PartnerFilterDropdown: FC<PartnerFilterDropdownProps> = ({
  value,
  onChange,
}: PartnerFilterDropdownProps) => {
  const { isLoading, data } = useGetPartnerAllList()

  const options = useMemo<PartnerFilterOption[]>(() => {
    const partners: PartnerFilterOption[] = (data?.partners ?? [])
      .filter((partner: any) => partner.isActive !== false)
      .map((partner: any) => ({
        id: partner.id,
        value: partner.id,
        label: partner.name,
        code: partner.code ?? '',
      }))
    return [ALL_PARTNERS_OPTION, ...partners]
  }, [data])

  return (
    <div className='w-[340px]'>
      <div className='text-[11.5px] font-bold text-N-500 tracking-wide mb-[6px]'>
        PARTNER
      </div>
      <SimpleSelect
        isLoading={isLoading}
        placeholder='Select partner…'
        value={value}
        onChange={onChange}
        options={options}
        formatOptionLabel={formatOptionLabel}
        filterOption={filterOption}
        isClearable={false}
      />
    </div>
  )
}

export default PartnerFilterDropdown
